import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { NeptuneApi } from '../genericApi/neptune.api';
import { NewswireApiModel } from './models/newswire-api.model';
import { NewswireReleaseSummaryModel } from './models/newswire-release-summary.model';
import { HttpVerbsEnum } from '../shared/enums/http-verbs.enum';
import { WireQuotaApiModel } from './models/wire-quota-api.model';
import { ReleaseStatsApiModel } from './models/release-stats-api.model';
import { HttpHeaders } from '@angular/common/http';
import { ReleaseStatsModel } from '../../Amplify/Wire/models/release-stats.model';
import { MultiBaseAbstractApi } from '../genericApi/multi-base-abstract.api';
import { DisplayGroupPatchModel } from '../displayGroups/models/display-group-patch.api.model';

export type WirePatchModel = Partial<NewswireApiModel> & Pick<NewswireApiModel, 'id'>;

@Injectable()
export class WireApi extends MultiBaseAbstractApi<NewswireApiModel, NewswireReleaseSummaryModel>  {
    constructor(
        private neptuneApi: NeptuneApi
    ) {
        super();
    }

    getById(wireId: number): Observable<NewswireApiModel> {
        const slug: string = `/newswire/releases/${wireId}`;

        return this.neptuneApi.api<NewswireApiModel>(
            slug,
            null,
            HttpVerbsEnum.GET
        );
    }

    editorGetById(wireId: number, isEditor: boolean = false): Observable<NewswireApiModel> {
        const slug = `/newswire/releases/review/${wireId}`;

        return this.neptuneApi.api<NewswireApiModel>(
            slug,
            null,
            HttpVerbsEnum.GET
        );
    }

    public getList(): Observable<NewswireReleaseSummaryModel[]> {
        const slug: string = '/newswire/releases/';

        return this.neptuneApi.api<NewswireReleaseSummaryModel[]>(
            slug,
            null,
            HttpVerbsEnum.GET
        );
    }

    public getReadyToReviewWireList(): Observable<NewswireReleaseSummaryModel[]> {
        const slug: string = '/newswire/releases/review';

        return this.neptuneApi.api<NewswireReleaseSummaryModel[]>(
            slug,
            null,
            HttpVerbsEnum.GET
        );
    }

    public create(wire: NewswireApiModel): Observable<number> {
        const slug: string = '/newswire/releases';

        return this.neptuneApi.api<number>(
            slug,
            wire,
            HttpVerbsEnum.POST
        );
    }

    public patch(wire: WirePatchModel, isEditor: boolean): Observable<number> {
        let slug = `/newswire/releases/${wire.id}`;

        if (isEditor) {
            slug = `/newswire/releases/review/${wire.id}`;
        }

        return this.neptuneApi.api<number>(
            slug,
            wire,
            HttpVerbsEnum.PATCH
        );
    }

    public update(wire: NewswireApiModel): Observable<number> {
        const slug: string = `/newswire/releases/${wire.id}`;

        return this.neptuneApi.api<number>(
            slug,
            wire,
            HttpVerbsEnum.PUT
        );
    }

    public editorUpdate(wire: NewswireApiModel): Observable<number> {
        const slug = `/newswire/releases/review/${wire.id}`;

        return this.neptuneApi.api<number>(
            slug,
            wire,
            HttpVerbsEnum.PUT
        );
    }

    public publish(wireId: number, bodyHtml: string): Observable<number> {
        const slug: string = `/newswire/releases/review/${wireId}/publish`;
        return this.neptuneApi.api<number>(
            slug,
            bodyHtml,
            HttpVerbsEnum.POST
        );
    }

    public deleteById(id: number): Observable<null> {
        const slug: string = `/newswire/releases/${id}`;

        return this.neptuneApi.api<null>(
            slug,
            null,
            HttpVerbsEnum.DELETE
        );
    }

    public getPerformanceReport(id: number): Observable<ReleaseStatsApiModel> {
        const slug: string = `/newswire/releases/${id}/stats`;

        return this.neptuneApi.api<ReleaseStatsModel>(
            slug,
            null,
            HttpVerbsEnum.GET
        );
    }

    public getCSVReport(wireId: number, currentDate: string): Observable<any> {
        const slug: string = `/newswire/releases/${wireId}/stats`;

        let headers = new HttpHeaders();
        headers = headers.set('accept', 'application/zip');

        return this.neptuneApi.api<any>(
            slug,
            null,
            HttpVerbsEnum.GET,
            {
                currentDate: currentDate
            },
            'blob',
            headers
        );
    }


    public getMyQuotas(): Observable<WireQuotaApiModel[]> {
        const slug: string = '/newswire/quotas/mine';

        return this.neptuneApi.api<WireQuotaApiModel[]>(
            slug,
            null,
            HttpVerbsEnum.GET
        );
    }

    // Admin
    public getQuotas(accountId: number): Observable<WireQuotaApiModel[]> {
        const slug: string = `/newswire/quotas/${accountId}`;

        return this.neptuneApi.api<WireQuotaApiModel[]>(
            slug,
            null,
            HttpVerbsEnum.GET
        );
    }

    // Admin
    public saveQuotas(accountId: number, quotas: WireQuotaApiModel[]): Observable<void> {
        const slug: string = `/newswire/quotas/${accountId}`;

        return this.neptuneApi.api<any>(
            slug,
            quotas,
            HttpVerbsEnum.POST
        );
    }

    /**
     * Adds and/or removes newswires from provided group
     * @param addIds Wires to add to group
     * @param removeIds Wires to remove from group
     * @param groupId
     * @returns
     */
    public updateNewswireDisplayGroup(addIds: number[], removeIds: number[], groupId: number) {
        const slug: string = `/newswire/releases/displaygroups/${groupId}`;
        const patchModel: DisplayGroupPatchModel = {
            addResourceIds: addIds,
            removeResourceIds: removeIds
        };
        return this.neptuneApi.api<void>(
            slug,
            patchModel,
            HttpVerbsEnum.PATCH
        );
    }

    /**
     * Removes group ID from all provided newswires
     * @param removeIds Wires to remove from groups
     * @returns
     */
    public removeNewswiresFromDisplayGroup(removeIds: number[]) {
        const slug: string = '/newswire/releases/displaygroups';
        const patchModel: DisplayGroupPatchModel = {
            addResourceIds: [],
            removeResourceIds: removeIds
        };
        return this.neptuneApi.api<void>(
            slug,
            patchModel,
            HttpVerbsEnum.PATCH
        );
    }

    public updateNewswireFavorites(addIds: number[], removeIds: number[]) {
        const slug: string = '/newswire/releases/displaygroups/favorites';
        const patchModel: DisplayGroupPatchModel = {
            addResourceIds: addIds,
            removeResourceIds: removeIds
        };
        return this.neptuneApi.api<void>(
            slug,
            patchModel,
            HttpVerbsEnum.PATCH
        );
    }
}
